<template>
  <AM3Modal
    :is-open="isOpen"
    width="sm"
    :header-title="title"
    :header-description="props.action !== 'create' ? `${formattedFanCount} contact${formattedFanCount > 1 ? 's' : ''} selected` : undefined"
    @close="handleClose"
  >
    <template #body>
      <div class="mt-1" />

      <template v-if="action === 'create'">
        <AM3InputText
          v-model="newTagName"
          label="Tag name"
          placeholder="Enter a tag name"
          required
          class="mb-2"
        />
        <div
          v-if="newTagName.trim()"
          class="flex items-center gap-2 mb-4"
        >
          <div class="size-8 shrink-0">
            <FavoriteButton v-model="newTagFavourite" />
          </div>
          <span class="text-sm text-secondary">Mark new tag as favourite</span>
        </div>
      </template>

      <AM3InputTags
        v-else
        ref="tagsInputRef"
        v-model="selectedTagObjects"
        :label="action === 'add' ? 'Selected tags' : 'Tags to remove'"
        placeholder="Search or create tags"
        :options="tags"
        :multiple="true"
        :allow-create="action === 'add'"
        :on-create-tag="handleCreateTag"
        :page-size="PAGE_SIZE"
        class="mb-4"
        @search="handleTagSearch"
        @options-fetched="handleTagsFetched"
        @creating="handleTagCreating"
      />

      <!-- Available tags list -->
      <div
        v-if="action === 'add' || action === 'remove' || action === 'edit'"
        ref="scrollContainer"
        class="max-h-80 overflow-y-auto"
        @scroll="handleScroll"
      >
        <div
          v-for="tag in tags"
          :key="tag.oid"
          class="flex items-center justify-between p-4 my-4 cursor-pointer rounded-md border border-1"
          :class="[isTagSelected(tag) ? 'border-font-brand bg-brand-light' : 'border-surface-dark hover:bg-gray-50']"
          @click="toggleTag(tag)"
        >
          <div class="flex items-center">
            <AM3Icon
              type="tag"
              size="lg"
            />
            <span class="ml-4">{{ tag.name }}</span>
          </div>
          <AM3Checkbox
            :model-value="isTagSelected(tag)"
            :data-test-id="`tag-${tag.name}`"
            class="mr-2"
          />
        </div>

        <!-- Loading indicator -->
        <div
          v-if="isLoadingMore"
          class="flex justify-center items-center p-4"
        >
          <div class="animate-spin size-6 border-2 border-brand border-t-transparent rounded-full" />
        </div>

        <!-- No more results indicator -->
        <div
          v-else-if="!hasMoreTags && tags.length > 0"
          class="text-center text-sm text-font-weak p-4"
        >
          All tags loaded
        </div>

        <!-- No results -->
        <div
          v-else-if="tags.length === 0 && !isLoadingMore"
          class="text-center text-sm text-font-weak p-4"
        >
          No tags found
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <div :class="(validationErrors.selected || validationErrors.name) ? 'text-font-danger' : ''">
          <span v-if="action === 'create' && validationErrors.name">Tag name is required</span>
          <span
            v-else-if="isCreatingTag"
            class="flex items-center gap-2"
          >
            <span class="animate-spin size-3 border-2 border-brand border-t-transparent rounded-full" />
            Confirming new tag&hellip;
          </span>
          <span v-else-if="action !== 'create' && (selectedTagObjects.length || validationErrors.selected)">{{ selectedTagObjects.length }} tag{{ selectedTagObjects.length > 1 ? 's' : '' }} selected</span>
        </div>
        <div class="flex items-center gap-4">
          <AM3Button
            text="Cancel"
            type="tertiary"
            @click="handleClose"
          />
          <AM3Button
            :text="buttonText"
            type="primary"
            :disabled="isCreatingTag"
            @click="handleSaveClick"
          />
        </div>
      </div>
    </template>
  </AM3Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useStore } from 'vuex'
import FavoriteButton from './FavoriteButton.vue'
import { formatInteger, capitalizeFirstLetter } from '@/utils/helpers/index.js'
import { createTag } from '@/pages/audience/composables/useTagCreation.js'
import AM3InputTags from '@/components/am3-generic/inputs/advanced/AM3InputTags.vue'

interface Props {
  isOpen: boolean
  action: 'add' | 'remove' | 'edit' | 'create'
  selection?: {
    count?: number
    fanOids?: number[]
    filter?: any
    searchString?: string
  }
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  action: 'add',
  selection: () => ({}),
})

const emit = defineEmits<{
  'cancel': []
  'edit': []
  'update:isOpen': [val: boolean]
}>()

function handleClose() {
  emit('cancel')
  emit('update:isOpen', false)
}

const store = useStore()
const { $arNotification } = useNuxtApp()

// Reactive data
const tagsInputRef = ref<InstanceType<typeof AM3InputTags>>()
const selectedTagObjects = ref<number[]>([])
const newTagName = ref('')
const newTagFavourite = ref(false)
const isCreatingTag = ref(false)
const validationErrors = ref({
  selected: false,
  name: false,
})

const buttonText = computed(() => {
  switch (props.action) {
    case 'create':
      return 'Create'
    case 'add':
      return 'Add tags'
    case 'remove':
      return 'Remove tags'
    case 'edit':
      return 'Update tags'
    default:
      return 'Save'
  }
})

// Infinite scroll state
const isLoadingMore = ref(false)
const hasMoreTags = ref(true)
const scrollContainer = ref<HTMLElement>()

// Computed properties
const title = computed(() => {
  switch (props.action) {
    case 'create':
      return 'Create new tag'
    case 'add':
      return 'Add tags'
    case 'remove':
      return 'Remove tags'
    case 'edit':
      return 'Edit tags'
    default:
      return capitalizeFirstLetter(props.action)
  }
})

const audienceSelection = computed(() => store.state.audienceModule.audienceSelection)
const tagOptions = ref<Array<{ name: string; oid?: number; favourite?: boolean }>>([])
const allFetchedTags = ref<Array<{ name: string; oid?: number; favourite?: boolean }>>([])
const tags = computed(() => tagOptions.value)

const formattedFanCount = computed(() => props.selection.count ?? 0)

// Computed: Convert tag oids to tag names for the API
const selectedTags = computed(() =>
  selectedTagObjects.value
    .map(oid => allFetchedTags.value.find(t => t.oid === oid)?.name)
    .filter((name): name is string => name !== undefined),
)

// Methods
const PAGE_SIZE = 50
let latestTagSearch = ''
// Monotonic counter guarding against out-of-order responses. A plain
// string-equality check against latestTagSearch isn't enough: two requests
// for the IDENTICAL search text (e.g. retrying a search for a just-created
// tag while its indexing catches up) can still race on the network, and the
// slower/stale one can resolve after — and silently overwrite — the correct
// one, even though the search text never changed. Every call gets its own
// sequence number; a response is only applied if no newer call has been
// issued since, regardless of what text it was for.
let searchSequence = 0
const fetchTags = async (inputValue?: string, reload = true) => {
  if (isLoadingMore.value && !reload) return

  const requestId = ++searchSequence
  try {
    isLoadingMore.value = true
    const searchString = inputValue ? inputValue.trim() : ''

    const response = await store.dispatch('tagModule/FETCH_TAGS', {
      searchString: searchString || null,
      reload,
      top: PAGE_SIZE,
      skip: reload ? 0 : tagOptions.value.length,
      $orderby: [['sysCtime', 'desc']],
    })

    if (requestId !== searchSequence) return

    const fetchedTags = response?.rows || []

    if (reload) {
      tagOptions.value = fetchedTags
      if (!searchString) {
        allFetchedTags.value = fetchedTags
      }
    }
    else {
      tagOptions.value = [...tagOptions.value, ...fetchedTags]
      if (!searchString) {
        allFetchedTags.value = [...allFetchedTags.value, ...fetchedTags]
      }
    }

    hasMoreTags.value = fetchedTags.length >= PAGE_SIZE
  }
  finally {
    if (requestId === searchSequence) {
      isLoadingMore.value = false
    }
  }
}

const loadMoreTags = async () => {
  if (!hasMoreTags.value || isLoadingMore.value) return
  await fetchTags(latestTagSearch, false)
}

// Merge by oid rather than replace, so a tag only ever seen via a filtered
// search result stays resolvable later (selectedTags below needs the name
// for any selected oid, even after the search text changes again).
const mergeTagsByOid = (
  existing: Array<{ name: string; oid?: number; favourite?: boolean }>,
  incoming: Array<{ name: string; oid?: number; favourite?: boolean }>,
) => {
  const map = new Map(existing.map(tag => [tag.oid, tag]))
  incoming.forEach((tag) => {
    if (tag.oid != null) map.set(tag.oid, tag)
  })
  return [...map.values()]
}

// AM3InputTags already fetches from the server (debounced/paced by its own
// AM3InputSelect) for every search keystroke and create. Sync the checkbox
// list from its @options-fetched emit instead of issuing a second, duplicate
// FETCH_TAGS for the same search text.
const handleTagsFetched = (fetchedTags: Array<{ name: string; oid?: number; favourite?: boolean }>) => {
  tagOptions.value = fetchedTags
  hasMoreTags.value = fetchedTags.length >= PAGE_SIZE
  allFetchedTags.value = mergeTagsByOid(allFetchedTags.value, fetchedTags)
}

const handleTagSearch = (searchText: string) => {
  latestTagSearch = searchText
  if (!searchText || searchText.trim() === '') {
    // Instant local reset, no network call needed — AM3InputTags will also
    // fetch the unfiltered page itself and handleTagsFetched will sync again.
    tagOptions.value = allFetchedTags.value
    hasMoreTags.value = allFetchedTags.value.length >= PAGE_SIZE
  }
}

const handleCreateTag = async (tagName: string, favourite: boolean) => {
  const createdTag = await createTag(tagName, store, $arNotification, { favourite })
  if (createdTag) {
    allFetchedTags.value = [createdTag, ...allFetchedTags.value]
    tagOptions.value = [createdTag, ...tagOptions.value]
  }
  return createdTag
}

// AM3InputTags emits this the instant a create starts (name search + select
// is fast; create is not) — surface a "Confirming..." state on Save instead
// of letting the two silently race, per EC-4417.
const handleTagCreating = (pending: Promise<void>) => {
  isCreatingTag.value = true
  pending.finally(() => {
    isCreatingTag.value = false
  })
}

const isTagSelected = (tag: { name: string; oid?: number }) => {
  return tag.oid != null && selectedTagObjects.value.includes(tag.oid)
}

const toggleTag = (tag: { name: string; oid?: number }) => {
  if (tag.oid == null) return
  const index = selectedTagObjects.value.indexOf(tag.oid)
  if (index === -1) {
    selectedTagObjects.value.push(tag.oid)
  }
  else {
    selectedTagObjects.value.splice(index, 1)
  }
}

// Scroll handling
const handleScroll = async (event: Event) => {
  const container = event.target as HTMLElement
  const { scrollTop, scrollHeight, clientHeight } = container

  // Check if we're near the bottom (within 50px)
  const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50

  if (isNearBottom && hasMoreTags.value && !isLoadingMore.value) {
    await loadMoreTags()
  }
}

const handleSaveClick = async () => {
  if (props.action === 'create') {
    validationErrors.value.name = !newTagName.value.trim()
    if (validationErrors.value.name) return

    try {
      const createdTag = await createTag(newTagName.value.trim(), store, $arNotification, { favourite: newTagFavourite.value })
      if (createdTag) {
        emit('edit')
        emit('update:isOpen', false)
      }
    }
    catch (error) {
      console.error('Error creating tag:', error)
    }
    return
  }

  // Belt-and-suspenders: if Save is clicked at the exact instant a create is
  // still in flight (isCreatingTag disables the button, but guard here too in
  // case this fires before that reactivity settles), wait for it so
  // selectedTagObjects reflects the new tag's oid before we read it below.
  await tagsInputRef.value?.createPending()

  validationErrors.value.selected = selectedTagObjects.value.length === 0
  if (validationErrors.value.selected) {
    return
  }

  try {
    await store.dispatch('audienceModule/MASS_EDIT_FAN', {
      fanOids: props.selection?.fanOids,
      filter: props.selection?.filter,
      searchString: props.selection?.searchString,
      tags: {
        [props.action]: selectedTags.value,
      },
    })

    $arNotification.push({
      type: 'success',
      message: `Successfully updated ${props.selection?.fanOids ? props.selection.fanOids.length.toLocaleString() : 'selected'} contacts.`,
    })

    emit('edit')
    emit('update:isOpen', false)
  }
  catch (e) {
    console.error(e)
    $arNotification.push({
      type: 'error',
      message: typeof (e) === 'string' ? e : 'There was a problem processing your request',
    })
  }
}

// Watch for modal open/close
watch(() => props.isOpen, (val) => {
  if (val) {
    selectedTagObjects.value = []
    newTagName.value = ''
    newTagFavourite.value = false
    validationErrors.value.selected = false
    validationErrors.value.name = false
    if (props.action !== 'create') {
      latestTagSearch = ''
      fetchTags('', true)
    }
  }
}, { immediate: true })

// Lifecycle
onMounted(() => {
  // Initial load will be handled by the watch on isOpen
})

onBeforeUnmount(() => {
  tagOptions.value = []
  allFetchedTags.value = []
})
</script>
